import React, { useState, useEffect } from 'react';
import { api } from '../api/client';

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

export default function TickDashboard() {
  const [summary, setSummary] = useState(null);
  const [byUser, setByUser] = useState([]);
  const [byStrain, setByStrain] = useState([]);
  const [tickList, setTickList] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      api.get('/admin/ticks/summary'),
      api.get('/admin/ticks/by-user'),
      api.get('/admin/ticks/by-strain'),
      api.get('/admin/ticks/list'),
    ]).then(([s, bu, bs, tl]) => {
      setSummary(s);
      setByUser(bu);
      setByStrain(bs);
      setTickList(tl);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const handleComplete = async (bookingId) => {
    try {
      await api.put(`/bookings/${bookingId}/status`, { status: 'completed' });
      fetchData();
    } catch {
      // ignore
    }
  };

  const handleCancel = async (bookingId) => {
    try {
      await api.put(`/bookings/${bookingId}/status`, { status: 'cancelled' });
      fetchData();
    } catch {
      // ignore
    }
  };

  if (loading) return <div className="card"><div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>Loading...</div></div>;
  if (!summary || (parseInt(summary.outstanding_count) === 0 && tickList.length === 0)) return null;

  return (
    <div className="card">
      <h3 style={{ marginBottom: 12 }}>Tick Management</h3>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 16 }}>
        <div className="stat-card" style={{ padding: 12 }}>
          <div className="stat-value" style={{ fontSize: 20, color: 'var(--danger)' }}>
            {parseFloat(summary.total_owed).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
          </div>
          <div className="stat-label">Total Owed</div>
        </div>
        <div className="stat-card" style={{ padding: 12 }}>
          <div className="stat-value" style={{ fontSize: 20 }}>{summary.outstanding_count}</div>
          <div className="stat-label">Outstanding</div>
        </div>
      </div>

      {/* By User */}
      {byUser.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>By User</h4>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Owed</th>
                  <th>Ticks</th>
                  <th>Oldest</th>
                </tr>
              </thead>
              <tbody>
                {byUser.map(u => (
                  <tr key={u.user_id}>
                    <td><strong>{u.username}</strong></td>
                    <td style={{ color: 'var(--danger)' }}>{parseFloat(u.amount_owed).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                    <td>{u.tick_count}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{timeAgo(u.oldest_tick)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* By Strain */}
      {byStrain.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>By Strain</h4>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Strain</th>
                  <th>Grams Owed</th>
                  <th>Value Owed</th>
                </tr>
              </thead>
              <tbody>
                {byStrain.map((s, i) => (
                  <tr key={i}>
                    <td><strong>{s.strain_name}</strong></td>
                    <td>{parseFloat(s.grams_owed)}g</td>
                    <td style={{ color: 'var(--danger)' }}>{parseFloat(s.value_owed).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Individual Ticks */}
      {tickList.length > 0 && (
        <div>
          <h4 style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>Outstanding Ticks</h4>
          <div style={{ overflowX: 'auto', maxHeight: 300, overflowY: 'auto' }}>
            <table style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Strain</th>
                  <th>Amount</th>
                  <th>Owed</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tickList.map(t => (
                  <tr key={t.id}>
                    <td><strong>{t.username}</strong></td>
                    <td style={{ color: 'var(--text-secondary)' }}>{t.strain_name || '-'}</td>
                    <td>{t.booking_amount}</td>
                    <td style={{ color: 'var(--danger)' }}>{parseFloat(t.tick_amount).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{timeAgo(t.created_at)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn-success btn-small" onClick={() => handleComplete(t.booking_id)}>
                          Paid
                        </button>
                        <button className="btn-danger btn-small" onClick={() => handleCancel(t.booking_id)} style={{ padding: '4px 8px', fontSize: 11 }}>
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
